import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { API_BASE_URL } from '../config/api';
import { ArrowLeft, Upload, CheckCircle, AlertCircle } from 'lucide-react';

export default function MembershipApplication() {
  const navigate = useNavigate();
  const location = useLocation();
  const { plan } = location.state || {};

  const [memberType, setMemberType] = useState('individual');
  const [formData, setFormData] = useState({
    // Common fields
    name: '',
    email: '',
    phone: '',
    purpose_category: '',
    purpose_details: '',
    expectations: '',
    
    // Individual fields
    designation: '',
    organization_affiliation: '',
    linkedin_profile: '',
    photo_url: '',
    
    // Institutional fields
    organization_name: '',
    organization_type: '',
    website_url: '',
    representing_executive_name: '',
    representing_executive_designation: '',
    num_employees: '',
    nature_of_business: '',
    logo_url: ''
  });

  const [purposeCategories, setPurposeCategories] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Email OTP verification states
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState('');

  useEffect(() => {
    // Fetch purpose categories
    axios.get(`${API_BASE_URL}/api/membership-applications/purpose-categories`)
      .then(res => setPurposeCategories(res.data.categories))
      .catch(err => console.error('Failed to load categories:', err));

    // Set member type from plan if available
    if (plan) {
      setMemberType(plan.plan_type);
    }
  }, [plan]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSendOtp = async () => {
    if (!formData.email) {
      setOtpError('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setOtpError('Please enter a valid email address');
      return;
    }

    setSendingOtp(true);
    setOtpError('');

    try {
      await axios.post(`${API_BASE_URL}/api/auth/verify-email-for-registration?email=${encodeURIComponent(formData.email)}`);
      
      setOtpSent(true);
      setOtpError('');
    } catch (err) {
      setOtpError(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length !== 6) {
      setOtpError('Please enter the 6-digit OTP code');
      return;
    }

    setVerifyingOtp(true);
    setOtpError('');

    try {
      await axios.post(`${API_BASE_URL}/api/auth/verify-registration-otp?email=${encodeURIComponent(formData.email)}&otp_code=${otpCode}`);
      
      setEmailVerified(true);
      setOtpError('');
      setOtpSent(false);
    } catch (err) {
      setOtpError(err.response?.data?.detail || 'Invalid OTP code');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only JPG and PNG files are allowed');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      setError('File size must be less than 2MB');
      return;
    }

    setUploading(true);
    setError('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/membership-applications/upload-photo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      // Update form data with uploaded file URL
      if (memberType === 'individual') {
        setFormData(prev => ({ ...prev, photo_url: data.url }));
      } else {
        setFormData(prev => ({ ...prev, logo_url: data.url }));
      }

      setUploadedFileName(file.name);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Check if email is verified
    if (!emailVerified) {
      setError('Please verify your email address before submitting');
      setLoading(false);
      return;
    }

    try {
      const submitData = new FormData();
      
      // Common fields
      submitData.append('member_type', memberType);
      // Use correct name field based on member type
      submitData.append('name', memberType === 'individual' ? formData.name : formData.organization_name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('purpose_category', formData.purpose_category);
      submitData.append('purpose_details', formData.purpose_details);
      submitData.append('expectations', formData.expectations);
      submitData.append('selected_plan', plan?.plan_name || 'Professional Membership');
      submitData.append('plan_type', memberType);

      if (memberType === 'individual') {
        submitData.append('designation', formData.designation);
        submitData.append('organization_affiliation', formData.organization_affiliation || '');
        submitData.append('linkedin_profile', formData.linkedin_profile || '');
        submitData.append('photo_url', formData.photo_url || '');
      } else {
        submitData.append('organization_name', formData.organization_name);
        submitData.append('organization_type', formData.organization_type);
        submitData.append('website_url', formData.website_url || '');
        submitData.append('representing_executive_name', formData.representing_executive_name);
        submitData.append('representing_executive_designation', formData.representing_executive_designation);
        submitData.append('num_employees', formData.num_employees || '');
        submitData.append('nature_of_business', formData.nature_of_business || '');
        submitData.append('logo_url', formData.logo_url || '');
        submitData.append('plan_tier', plan?.tier_level || 1);
      }

      const { data } = await axios.post(
        `${API_BASE_URL}/api/membership-applications/submit`,
        submitData
      );

      setSuccess(true);
      
      // Redirect to next steps page after 2 seconds
      setTimeout(() => {
        navigate('/membership-next-steps', { 
          state: { 
            applicationId: data.application_id,
            plan: plan,
            memberType: memberType
          } 
        });
      }, 2000);

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-16">
          <Card className="max-w-2xl mx-auto">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">
                Application Submitted Successfully!
              </h2>
              <p className="text-gray-600 mb-4">
                Thank you for applying for CSED membership. Redirecting to next steps...
              </p>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#1E3A5F]/90 text-white py-16 pt-32">
        <div className="container mx-auto px-4">
          <button
            onClick={() => navigate('/membership')}
            className="flex items-center text-white hover:text-[#E87722] mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Membership Plans
          </button>
          <h1 className="text-4xl font-bold mb-4">Membership Application</h1>
          <p className="text-gray-200 max-w-3xl">
            {plan ? `Applying for: ${plan.plan_name}` : 'Complete your membership application'}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle>
              <div className="flex gap-4 mb-6">
                <button
                  type="button"
                  onClick={() => setMemberType('individual')}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                    memberType === 'individual'
                      ? 'bg-[#E87722] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Individual Professional
                </button>
                <button
                  type="button"
                  onClick={() => setMemberType('institutional')}
                  className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                    memberType === 'institutional'
                      ? 'bg-[#E87722] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Institutional Member
                </button>
              </div>
            </CardTitle>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {/* Common Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">
                    {memberType === 'individual' ? 'Full Name' : 'Organization Name'} *
                  </Label>
                  <Input
                    id="name"
                    name={memberType === 'individual' ? 'name' : 'organization_name'}
                    value={memberType === 'individual' ? formData.name : formData.organization_name}
                    onChange={handleChange}
                    required
                    placeholder={memberType === 'individual' ? 'John Doe' : 'Acme Corporation'}
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <div className="flex gap-2">
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => {
                        handleChange(e);
                        setEmailVerified(false);
                        setOtpSent(false);
                      }}
                      required
                      placeholder="email@example.com"
                      disabled={emailVerified}
                      className={emailVerified ? 'bg-green-50 border-green-500' : ''}
                    />
                    {!emailVerified && !otpSent && (
                      <Button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={sendingOtp || !formData.email}
                        className="bg-[#E87722] hover:bg-[#d66a1a] whitespace-nowrap"
                      >
                        {sendingOtp ? 'Sending...' : 'Verify Email'}
                      </Button>
                    )}
                    {emailVerified && (
                      <span className="flex items-center text-green-600 text-sm font-medium whitespace-nowrap">
                        <CheckCircle className="w-5 h-5 mr-1" />
                        Verified
                      </span>
                    )}
                  </div>
                  
                  {/* OTP Input Section */}
                  {otpSent && !emailVerified && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800 mb-2">
                        OTP sent to {formData.email}. Please enter the 6-digit code:
                      </p>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          placeholder="000000"
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          maxLength={6}
                          className="max-w-[150px]"
                        />
                        <Button
                          type="button"
                          onClick={handleVerifyOtp}
                          disabled={verifyingOtp || otpCode.length !== 6}
                          className="bg-[#E87722] hover:bg-[#d66a1a]"
                        >
                          {verifyingOtp ? 'Verifying...' : 'Verify'}
                        </Button>
                        <Button
                          type="button"
                          onClick={handleSendOtp}
                          disabled={sendingOtp}
                          variant="outline"
                        >
                          Resend OTP
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {otpError && (
                    <p className="text-sm text-red-600 mt-1">{otpError}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+91 98765 43210"
                  />
                </div>

                {/* Conditional Fields */}
                {memberType === 'individual' ? (
                  <div>
                    <Label htmlFor="designation">Designation/Title *</Label>
                    <Input
                      id="designation"
                      name="designation"
                      value={formData.designation}
                      onChange={handleChange}
                      required
                      placeholder="e.g., Senior Consultant"
                    />
                  </div>
                ) : (
                  <div>
                    <Label htmlFor="organization_type">Organization Type *</Label>
                    <select
                      id="organization_type"
                      name="organization_type"
                      value={formData.organization_type}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E87722]"
                    >
                      <option value="">Select type...</option>
                      <option value="NGO">NGO</option>
                      <option value="Corporate">Corporate</option>
                      <option value="Government">Government</option>
                      <option value="Academic">Academic</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Individual-specific fields */}
              {memberType === 'individual' && (
                <>
                  <div>
                    <Label htmlFor="organization_affiliation">
                      Organization/Institution (if any)
                    </Label>
                    <Input
                      id="organization_affiliation"
                      name="organization_affiliation"
                      value={formData.organization_affiliation}
                      onChange={handleChange}
                      placeholder="e.g., ABC Audit Firm, XYZ Legal Associates"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Especially for Audit/Legal firms
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="linkedin_profile">LinkedIn Profile (Optional)</Label>
                    <Input
                      id="linkedin_profile"
                      name="linkedin_profile"
                      type="url"
                      value={formData.linkedin_profile}
                      onChange={handleChange}
                      placeholder="https://linkedin.com/in/yourprofile"
                    />
                  </div>
                </>
              )}

              {/* Institutional-specific fields */}
              {memberType === 'institutional' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="representing_executive_name">
                        Representing Executive Name *
                      </Label>
                      <Input
                        id="representing_executive_name"
                        name="representing_executive_name"
                        value={formData.representing_executive_name}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <Label htmlFor="representing_executive_designation">
                        Executive Designation *
                      </Label>
                      <Input
                        id="representing_executive_designation"
                        name="representing_executive_designation"
                        value={formData.representing_executive_designation}
                        onChange={handleChange}
                        required
                        placeholder="CEO, Director, etc."
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="website_url">Website URL</Label>
                      <Input
                        id="website_url"
                        name="website_url"
                        type="url"
                        value={formData.website_url}
                        onChange={handleChange}
                        placeholder="https://www.yourorganization.com"
                      />
                    </div>

                    <div>
                      <Label htmlFor="num_employees">Number of Employees</Label>
                      <select
                        id="num_employees"
                        name="num_employees"
                        value={formData.num_employees}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E87722]"
                      >
                        <option value="">Select range...</option>
                        <option value="1-10">1-10</option>
                        <option value="11-50">11-50</option>
                        <option value="51-200">51-200</option>
                        <option value="201-500">201-500</option>
                        <option value="500+">500+</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="nature_of_business">Nature of Business</Label>
                    <Input
                      id="nature_of_business"
                      name="nature_of_business"
                      value={formData.nature_of_business}
                      onChange={handleChange}
                      placeholder="e.g., Social Development, Education, Healthcare"
                    />
                  </div>
                </>
              )}

              {/* Purpose Section */}
              <div>
                <Label htmlFor="purpose_category">
                  Purpose for Joining CSED *
                </Label>
                <select
                  id="purpose_category"
                  name="purpose_category"
                  value={formData.purpose_category}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#E87722]"
                >
                  <option value="">Select purpose...</option>
                  {purposeCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              {formData.purpose_category && (
                <div>
                  <Label htmlFor="purpose_details">
                    Please elaborate on your purpose *
                  </Label>
                  <Textarea
                    id="purpose_details"
                    name="purpose_details"
                    value={formData.purpose_details}
                    onChange={handleChange}
                    required
                    rows={4}
                    placeholder="Tell us more about why you want to join and what you hope to achieve..."
                  />
                </div>
              )}

              <div>
                <Label htmlFor="expectations">
                  Your Expectations from CSED *
                </Label>
                <Textarea
                  id="expectations"
                  name="expectations"
                  value={formData.expectations}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder="What do you expect from CSED membership? How can we support your goals?"
                />
              </div>

              {/* File Upload */}
              <div>
                <Label htmlFor="file_upload">
                  {memberType === 'individual' ? 'Upload Your Photo' : 'Upload Organization Logo'}
                </Label>
                <div className="mt-2">
                  <label
                    htmlFor="file_upload"
                    className="flex items-center justify-center w-full h-32 px-4 transition border-2 border-gray-300 border-dashed rounded-lg appearance-none cursor-pointer hover:border-[#E87722] focus:outline-none"
                  >
                    <div className="flex flex-col items-center space-y-2">
                      <Upload className="w-8 h-8 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {uploading
                          ? 'Uploading...'
                          : uploadedFileName
                          ? `Uploaded: ${uploadedFileName}`
                          : 'Click to upload or drag and drop'}
                      </span>
                      <span className="text-xs text-gray-500">
                        JPG or PNG, max 2MB (auto-optimized to 800x800px)
                      </span>
                    </div>
                    <input
                      id="file_upload"
                      type="file"
                      className="hidden"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleFileUpload}
                      disabled={uploading}
                    />
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-6 border-t">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#E87722] hover:bg-[#d66a1a] text-white py-3 text-lg"
                >
                  {loading ? 'Submitting...' : 'Submit Application'}
                </Button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  After submission, you can proceed with payment or book an appointment
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      <Footer />
    </div>
  );
}
